import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Lottie from 'lottie-react';
import menuOpenAnimation from '../assets/icons/menuOpen.json';
import menuCloseAnimation from '../assets/icons/menuClose.json';
import logo from '../assets/images/INDIGENOUS MAASAI CLIMATE ADAPTATION ALLIANCE (IMCAA) - Logo.webp';

const Navbar = () => {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const menuRef = useRef(null);
  const buttonRef = useRef(null);
  
  // Handle scroll events to change navbar style when scrolled
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 10) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);
  
  // Handle outside click to close menu
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (isOpen && menuRef.current && !menuRef.current.contains(e.target) && !buttonRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleOutsideClick);
    
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [isOpen]);
  
  // Close menu when location changes
  useEffect(() => {
    setIsOpen(false);
    setActiveDropdown(null);
  }, [location]);
  
  // Handle keyboard navigation
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        if (isOpen) {
          setIsOpen(false);
        }
        if (activeDropdown) {
          setActiveDropdown(null);
        }
      }
    };
    
    window.addEventListener('keydown', handleEscape);
    return () => {
      window.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, activeDropdown]);

  // Main navigation links and structure
  const navStructure = [
    { name: 'Home', path: '/' },
    { 
      name: 'Our Story', 
      path: null,
      children: [
        { name: 'Who We Are', path: '/WhoWeAre' },
        { name: 'Connect With Us', path: '/ConnectWithUs' },
      ]
    },
    { 
      name: 'Our Operations', 
      path: null,
      children: [
        { name: 'Programmes', path: '/Programmes' },
        { name: 'Projects', path: '/Projects' },
        { name: 'Events & News', path: '/EventsAndNews' },
      ]
    },
    { name: 'Walk With Us', path: '/WalkWithUs' },
  ];
  
  // Helper to check if a path is active, including checking children
  const isPathActive = (item) => {
    if (item.path && location.pathname === item.path) {
      return true;
    }
    
    if (item.children) {
      return item.children.some(child => location.pathname === child.path);
    }
    
    return false;
  };

  // Toggle dropdown menu
  const toggleDropdown = (index, e) => {
    e && e.preventDefault();
    setActiveDropdown(activeDropdown === index ? null : index);
  };

  return (
    <>
      {/* Desktop Navigation */}
      <header 
        className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
          scrolled ? 'bg-white shadow-md' : 'bg-white'
        }`}
      >
        <div className="container mx-auto px-4 flex items-center justify-between h-16 lg:h-20">
          {/* Logo */}
          <Link 
            to="/" 
            className="flex items-center border-grey-950"
            aria-label="IMCAA Home"
          >
            <div className="overflow-hidden rounded-full h-10 w-10 flex-shrink-0 border !border-[#008000] shadow-sm">
              <img 
                src={logo} 
                alt="IMCAA Logo" 
                className="h-full w-full object-cover"
              />
            </div>
            <span className="ml-2 text-lg lg:text-xl font-bold text-forest-green">
              IMCAA
            </span>
          </Link>
          
          {/* Mobile Menu Button */}
          <button
            ref={buttonRef}
            onClick={() => setIsOpen(!isOpen)}
            className="lg:hidden flex items-center p-2"
            aria-expanded={isOpen}
            aria-controls="mobile-menu"
            aria-label={isOpen ? "Close navigation menu" : "Open navigation menu"}
          >
            <div className="w-6 h-6">
              {isOpen ? (
                <Lottie animationData={menuCloseAnimation} loop={false} autoplay={true} />
              ) : (
                <Lottie animationData={menuOpenAnimation} loop={false} autoplay={true} />
              )}
            </div>
          </button>
          
          {/* Desktop Nav Links */}
          <nav className="hidden lg:flex items-center">
            <ul className="flex items-center space-x-6">
              {navStructure.map((item, index) => (
                <li key={index} className="relative group">
                  {item.path ? (
                    <Link 
                      to={item.path}
                      className={`py-2 inline-block ${
                        isPathActive(item) ? 'text-base-green border-b-2 border-base-green' : 'text-gray-700 hover:text-base-green'
                      }`}
                    >
                      {item.name}
                    </Link>
                  ) : (
                    <div className="relative">
                      <button
                        onClick={(e) => toggleDropdown(index, e)}
                        className={`flex items-center py-2 group ${
                          isPathActive(item) || activeDropdown === index ? 'text-base-green' : 'text-gray-700 hover:text-base-green'
                        }`}
                        aria-expanded={activeDropdown === index}
                      >
                        {item.name}
                        <svg
                          className={`ml-1 w-4 h-4 transition-transform ${
                            activeDropdown === index ? 'transform rotate-180' : ''
                          }`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M19 9l-7 7-7-7"
                          ></path>
                        </svg>
                      </button>
                      
                      {/* Desktop Dropdown */}
                      <div
                        className={`absolute left-0 mt-1 py-2 bg-white shadow-lg rounded-lg min-w-[180px] transition-all origin-top ${
                          activeDropdown === index ? 'scale-y-100 opacity-100' : 'scale-y-0 opacity-0 pointer-events-none'
                        }`}
                      >
                        {item.children.map((child, childIndex) => (
                          <Link
                            key={childIndex}
                            to={child.path}
                            className={`block px-4 py-2 hover:bg-gray-50 ${
                              location.pathname === child.path ? 'text-base-green' : 'text-gray-700'
                            }`}
                          >
                            {child.name}
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </li>
              ))}
            </ul>
            
            {/* Donate Button */}
            <Link
              to="/Donate"
              className="ml-8 px-6 py-2 bg-base-green text-white font-medium rounded-md hover:bg-forest-green transition-colors"
              aria-label="Donate to IMCAA"
            >
              Donate <span aria-hidden="true">→</span>
            </Link>
          </nav>
        </div>
      </header>
      
      {/* Mobile Menu */}
      <div 
        id="mobile-menu"
        ref={menuRef}
        className={`lg:hidden fixed inset-0 bg-white z-50 transition-all duration-300 ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="h-full flex flex-col overflow-y-auto">
          {/* Mobile Header */}
          <div className="flex items-center justify-between p-4 border-b">
            <div className="flex items-center">
              <div className="overflow-hidden rounded-full h-10 w-10 flex-shrink-0 border !border-[#008000] shadow-sm">
                <img 
                  src={logo} 
                  alt="IMCAA Logo" 
                  className="h-full w-full object-cover"
                />
              </div>
              <span className="ml-2 text-lg font-bold text-forest-green">
                IMCAA
              </span>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="p-2"
              aria-label="Close menu"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          {/* Mobile Nav Links */}
          <div className="flex-grow">
            <div className="py-2 border-b">
              <Link
                to="/"
                className={`block px-4 py-3 ${
                  location.pathname === '/' ? 'text-base-green' : 'text-gray-800'
                }`}
              >
                Home
              </Link>
            </div>
            
            {navStructure.slice(1).map((item, index) => (
              <div key={index} className="border-b">
                {item.path ? (
                  <Link
                    to={item.path}
                    className={`block px-4 py-3 ${
                      location.pathname === item.path ? 'text-base-green' : 'text-gray-800'
                    }`}
                  >
                    {item.name}
                  </Link>
                ) : (
                  <>
                    <button
                      onClick={(e) => toggleDropdown(index + 1, e)}
                      className="flex items-center justify-between w-full px-4 py-3 text-gray-800"
                    >
                      {item.name}
                      <svg
                        className={`w-5 h-5 transition-transform ${
                          activeDropdown === index + 1 ? 'transform rotate-180' : ''
                        }`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    
                    {/* Mobile Dropdown */}
                    <div className={`bg-gray-50 transition-all duration-300 overflow-hidden ${
                      activeDropdown === index + 1 ? 'max-h-96' : 'max-h-0'
                    }`}>
                      {item.children.map((child, childIndex) => (
                        <Link
                          key={childIndex}
                          to={child.path}
                          className={`block px-8 py-3 ${
                            location.pathname === child.path ? 'text-base-green' : 'text-gray-700'
                          }`}
                        >
                          {child.name}
                        </Link>
                      ))}
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
          
          {/* Mobile Donate Button */}
          <div className="p-4 border-t">
            <Link
              to="/Donate"
              className="block w-full py-3 bg-base-green text-center text-white font-medium rounded-md"
            >
              Donate Now
            </Link>
          </div>
        </div>
      </div>
    </>
  );
};

export default Navbar;